import { fustionColorPalette } from "../constant/fusion-color-pallete";

const palette = fustionColorPalette

export const onLightTextColor = palette["text-primary"];
export const onDarkTextColor = palette["text-inverse-primary"];

export const defaultCompareThemingDark = {
  page: {
    backgroundUrl: "https://www.directv.com/dtvassets/global/component-assets/in-page_clup/channel_background.jpg",
    backgroundColor: "#102641",
    theme: "onDark"
  },
  filterSection: {
    background: "#1A2A3A",
    theme: "onDark"
  },
  grid: {
    background: "#19253A",
    theme: "onDark",
    addOnPromoBackground: "#21528c",
    addOnPromoTheme: "onDark"
  }
};

export const defaultCompareThemingLight = {
  theme: "light",
  backgroundColor: "#ffffff",
  filterSectionBg: "#fff",
  sectionHeaders: {
    theme: "light",
    backgroundColor: "#D8E7F9"
  },
  selectedPkgBgColor: "#2E70BD",
  packageTiles: {
    entertainment: {
      backgroundColor: "#D8E7F9",
      textColor: "#102641"
    },
    "choice™": {
      backgroundColor: "#D8E7F9",
      textColor: "#102641"
    },
    ultimate: {
      backgroundColor: "#7DB3F2",
      textColor: "#102641"
    },
    "premier™": {
      backgroundColor: "#196DD4",
      textColor: "#FFFFFF"
    },
    "óptimo más™": {
      backgroundColor: "#E0F2FA",
      textColor: "#102641"
    }
  },
  grid: {
    theme: "light",
    background: "#fbfbfb",
    border: "#D1D1D1",
    highLightRowBackground: "#F4F4F4",
    highLightRowTextColor: "#102641"
  },
  pdfLinkColor: "#2e70bd"
};

export interface ThemeOption {
  theme: "onDark" | "ondark" | "dark" | "onLight" | "onlight" | "light";
  background?: string;
}

export interface IChannelCompareThemeUI {
  mode?: "dark" | "light";
  backgroundColor?: string;
  headerTextColor?: string;
  subheadingTextColor?: string;
  zipCodeModuleMode?: "dark" | "light";
  toggleBtnMode?: "dark" | "light";
  filterSectionBg?: string;
  filterSectionText?: string;
  sectionHeaders?: {
    backgroundColor?: string;
    textColor?: string;
  };
  selectedPkgBgColor?: string;
  packageTiles?: any;
  grid ?: {
    mode?: "dark" | "light";
    background?: string;
    textColor?: string;
    border?: string;
    highLightRowBackground?: string;
    highLightRowTextColor?: string;
  };
  pdfLinkColor?: string;
  scrollToTopBackground?: string;
};

const normalizeTheme = (theme: string): "dark" | "light" => {
  if (!theme) {
    return "dark";
  }
  return ["onlight", "light"].includes(theme.trim().toLowerCase()) ? "light" : "dark";
};

const getThemeText = (theme: string) => {
  if (!theme) {
    return onDarkTextColor;
  }
  return normalizeTheme(theme) === "light" ? onLightTextColor : onDarkTextColor;
};

const parseColor = (color: string) => {
  if (!color) {
    return "";
  }
  if (palette?.[color]) {
    return palette[color];
  }
  if (
    color.startsWith("#") ||
    ["rgb", "hsl", "transparent", "none"].some(other => color.toLowerCase().includes(other))
  ) {
    return color;
  }
  return "";
};

export function parseChannelCompareTheme(themeFeed: any): IChannelCompareThemeUI {
  try {
    const { sectionHeaders, filterSection, grid, packageTiles } = themeFeed || {};
    // Tolerate both theming-feed field names for a slot's color. Canonical
    // shapes (and flag-OFF / AEM) key a slot `background`; the flag-ON Storyblok
    // blok reuses the `page` component for every slot, which keys it
    // `backgroundColor`. Coalesce so both render. Mirrors the proven fix in
    // channel-lineup-theme.ts parseThemeObject; no-op when a slot uses `background`.
    const slotBg = (slot: any) => slot?.background ?? slot?.backgroundColor;
    if (themeFeed) {
      return {
        mode: normalizeTheme(themeFeed?.theme),
        backgroundColor: parseColor(themeFeed?.backgroundColor),
        headerTextColor: getThemeText(themeFeed?.theme),
        subheadingTextColor: getThemeText(themeFeed?.theme),
        zipCodeModuleMode: normalizeTheme(themeFeed?.theme),
        toggleBtnMode: normalizeTheme(themeFeed?.theme),
        filterSectionBg: parseColor(slotBg(filterSection)) || "#fff",
        filterSectionText: getThemeText(filterSection?.theme || themeFeed?.theme),
        sectionHeaders: {
          backgroundColor: parseColor(slotBg(sectionHeaders)) || "#D8E7F9",
          textColor: getThemeText(sectionHeaders?.theme || themeFeed?.theme),
        },
        selectedPkgBgColor: parseColor(themeFeed?.selectedPkgBgColor) || "#2E70BD",
        packageTiles: { ...packageTiles },
        grid: {
          mode: normalizeTheme(grid?.theme || themeFeed?.theme),
          border: parseColor(grid?.border) || "#D1D1D1",
          background: parseColor(slotBg(grid)) || "#fbfbfb",
          textColor: getThemeText(grid?.theme || themeFeed?.theme),
          highLightRowBackground: parseColor(grid?.highLightRowBackground) || "#F4F4F4",
          highLightRowTextColor: parseColor(grid?.highLightRowTextColor) || "#102641"
        },
        pdfLinkColor: themeFeed?.pdfLinkColor
      };
    }
    const { grid: defaultGrid } = defaultCompareThemingLight;
    return {
      mode: "light",
      backgroundColor: defaultCompareThemingLight?.backgroundColor,
      headerTextColor: onLightTextColor,
      subheadingTextColor: onLightTextColor,
      zipCodeModuleMode: "light",
      toggleBtnMode: "light",
      filterSectionBg: defaultCompareThemingLight?.filterSectionBg || "#F5F8FA",
      filterSectionText: onLightTextColor,
      selectedPkgBgColor: "#2E70BD",
      sectionHeaders: {
        backgroundColor: defaultCompareThemingLight?.sectionHeaders?.backgroundColor,
        textColor: onLightTextColor
      },
      packageTiles: {
        entertainment: {
          backgroundColor: "#D8E7F9",
          textColor: "#102641"
        },
        "choice™": {
          backgroundColor: "#D8E7F9",
          textColor: "#102641"
        },
        ultimate: {
          backgroundColor: "#7DB3F2",
          textColor: "#102641"
        },
        "premier™": {
          backgroundColor: "#196DD4",
          textColor: "#FFFFFF"
        },
        "óptimo más™": {
          backgroundColor: "#E0F2FA",
          textColor: "#102641"
        }
      },
      grid: {
        mode: "light",
        border: defaultCompareThemingLight?.grid?.border,
        background: defaultGrid?.background || "#FBFBFB",
        textColor: onLightTextColor,
        highLightRowBackground: defaultCompareThemingLight?.grid?.highLightRowBackground,
        highLightRowTextColor: defaultCompareThemingLight?.grid?.highLightRowTextColor
      },
      pdfLinkColor: palette.blue600
    };
  } catch (_error) {
    // Fallback to defaultCompareThemingLight if parsing fails
    const { grid: defaultGrid } = defaultCompareThemingLight;
    return {
      mode: "light",
      backgroundColor: defaultCompareThemingLight?.backgroundColor,
      headerTextColor: onLightTextColor,
      subheadingTextColor: onLightTextColor,
      zipCodeModuleMode: "light",
      toggleBtnMode: "light",
      filterSectionBg: defaultCompareThemingLight?.filterSectionBg || "#F5F8FA",
      filterSectionText: onLightTextColor,
      selectedPkgBgColor: "#2E70BD",
      sectionHeaders: {
        backgroundColor: defaultCompareThemingLight?.sectionHeaders?.backgroundColor,
        textColor: onLightTextColor
      },
      packageTiles: {
        entertainment: {
          backgroundColor: "#D8E7F9",
          textColor: "#102641"
        },
        "choice™": {
          backgroundColor: "#D8E7F9",
          textColor: "#102641"
        },
        ultimate: {
          backgroundColor: "#7DB3F2",
          textColor: "#102641"
        },
        "premier™": {
          backgroundColor: "#196DD4",
          textColor: "#FFFFFF"
        },
        "óptimo más™": {
          backgroundColor: "#E0F2FA",
          textColor: "#102641"
        }
      },
      grid: {
        mode: "light",
        border: defaultCompareThemingLight?.grid?.border,
        background: defaultGrid?.background || "#FBFBFB",
        textColor: onLightTextColor,
        highLightRowBackground: defaultCompareThemingLight?.grid?.highLightRowBackground,
        highLightRowTextColor: defaultCompareThemingLight?.grid?.highLightRowTextColor
      },
      pdfLinkColor: palette.blue600
    };
  }
}
